/**
 * /about — Attribution, credits, and project explanation.
 */

import { Link } from 'react-router-dom'
import { CandidateWarning } from '../components/CandidateWarning'
import { SiteFooter } from '../components/SiteFooter'

const GITHUB_URL =
  'https://github.com/ilprl/NepTam-A-Nepali-Tamang-Parallel-Corpus-and-Baseline-Machine-Translation-Experiments'
const ARXIV_URL = 'https://arxiv.org/abs/2603.14053'

export function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <CandidateWarning />

      {/* Nav */}
      <nav className="bg-tamang-700 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-white/80 hover:text-white text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Search
          </Link>
          <span className="text-white/40">·</span>
          <span className="text-white font-semibold text-sm">About &amp; Credits</span>
        </div>
      </nav>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10 space-y-10">

        {/* ── What is this site ──────────────────────────────────── */}
        <section className="space-y-4">
          <h1 className="text-3xl font-bold text-tamang-700">About This Project</h1>

          <div className="prose prose-stone max-w-none space-y-3 text-stone-700 leading-relaxed">
            <p>
              This is a <strong>community-review candidate dictionary</strong> for{' '}
              <strong>Eastern Tamang</strong>{' '}
              <span className="text-stone-400 font-mono text-sm">(ISO 639-3: taj)</span>,
              not a fully verified linguistic resource.
            </p>
            <p>
              Dictionary candidates were generated <em>automatically</em> from a
              20,000-sentence Nepali–Tamang parallel corpus using word-alignment models
              (eflomal, awesome-align) and machine-translation glossing (NLLB-200-3.3B).
              No human linguist has verified these entries.
            </p>
            <p>
              <strong>Public suggestions</strong> submitted through this site are stored
              separately in a review queue. They do not immediately change dictionary
              entries. All suggestions are reviewed before any edits are accepted.
            </p>
            <p>
              We strongly encourage <strong>native Tamang speakers</strong> to review
              entries and submit corrections. Community feedback is the primary path to
              improving accuracy.
            </p>
          </div>
        </section>

        {/* ── Data attribution ──────────────────────────────────── */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-tamang-700">Data Attribution</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
            <p className="text-stone-700 leading-relaxed">
              This project uses data derived from{' '}
              <strong>
                NepTam: A Nepali-Tamang Parallel Corpus and Baseline Machine Translation
                Experiments
              </strong>{' '}
              by Rupak Raj Ghimire, Bipesh Subedi, Balaram Prasain, Prakash Poudyal,
              Praveen Acharya, Nischal Karki, Rupak Tiwari, Rishikesh Kumar Sharma,
              Jenny Poudel, and Bal Krishna Bal.
            </p>
            <p className="text-stone-700 leading-relaxed">
              The original corpus and baseline MT experiments are available at the NepTam
              GitHub repository and arXiv paper.
            </p>

            <div className="flex flex-wrap gap-3 pt-1">
              <ExternalLink href={GITHUB_URL} label="NepTam GitHub Repository" icon="📦" />
              <ExternalLink href={ARXIV_URL}  label="arXiv Paper (2603.14053)" icon="📄" />
            </div>
          </div>

          <div className="text-sm text-stone-500 space-y-1">
            <p>
              <strong>This community review interface is an independent tool</strong> built
              on derived dictionary candidates. It is not affiliated with, endorsed by, or
              produced by the original NepTam corpus authors.
            </p>
            <p>
              Please cite the original NepTam paper when referencing the underlying corpus data.
            </p>
          </div>
        </section>

        {/* ── How candidates were generated ────────────────────── */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-tamang-700">How Entries Were Generated</h2>

          <ol className="space-y-3 text-stone-700">
            <Step n={1} title="Source corpus">
              20,000 Nepali–Tamang parallel sentence pairs from the NepTam corpus.
            </Step>
            <Step n={2} title="Word alignment">
              Candidate Tamang–Nepali word pairs were extracted using two independent
              alignment methods: <em>eflomal</em> (statistical) and{' '}
              <em>awesome-align</em> (neural). Pairs confirmed by both methods are marked
              High Priority.
            </Step>
            <Step n={3} title="English glossing">
              English glosses were generated automatically using the{' '}
              <em>NLLB-200-3.3B</em> machine-translation model. Glosses are marked
              "auto-unverified" and are frequently incorrect or incomplete.
            </Step>
            <Step n={4} title="Status flags">
              Each entry carries a confidence status:{' '}
              <strong>High Priority</strong> (both aligners agree),{' '}
              <strong>Unverified</strong> (one aligner),{' '}
              <strong>Needs Review</strong> (aligners disagree), or{' '}
              <strong>Low Priority</strong> (position-only alignment).
            </Step>
            <Step n={5} title="Human review pending">
              None of these candidates have been validated by a native Tamang speaker.
              This site is the validation interface.
            </Step>
          </ol>
        </section>

        {/* ── How to help ──────────────────────────────────────── */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-tamang-700">How to Help</h2>
          <div className="space-y-2 text-stone-700">
            <p>
              If you are a Tamang speaker or linguist, you can help by:
            </p>
            <ul className="list-disc list-inside space-y-1 text-stone-600 ml-2">
              <li>Searching for words you know and checking their Tamang translations.</li>
              <li>
                Clicking <strong>"Suggest a Correction"</strong> on any entry that is
                wrong, incomplete, or missing a dialect variant.
              </li>
              <li>Submitting dialect notes — Tamang has regional varieties and this matters.</li>
              <li>Flagging entries marked "Needs Review" that need expert attention.</li>
            </ul>
            <p className="mt-2">
              Your submissions are reviewed before any change is made to the dictionary.
              You can always leave your name and email if you want to be credited for your
              contribution.
            </p>
          </div>
          <p className="mt-4 text-stone-600">
            Have questions or want to get involved?{' '}
            <a
              href="mailto:info@tamang-dictionary.org"
              className="underline text-tamang-700 hover:text-tamang-900"
            >
              Write to us at info@tamang-dictionary.org
            </a>
          </p>
          <Link to="/" className="btn-primary inline-flex mt-2">
            Start searching →
          </Link>
        </section>

      </main>

      <SiteFooter />
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ExternalLink({ href, label, icon }: { href: string; label: string; icon: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-300
                 bg-white text-sm text-stone-700 hover:bg-stone-50 hover:border-tamang-600
                 transition-colors shadow-sm"
    >
      <span>{icon}</span>
      <span className="underline">{label}</span>
      <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-tamang-100 text-tamang-700
                       flex items-center justify-center text-sm font-bold mt-0.5">
        {n}
      </span>
      <div>
        <strong className="text-stone-800">{title}.</strong>{' '}
        <span className="text-stone-600">{children}</span>
      </div>
    </li>
  )
}
